import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress,
  Alert, Chip, Divider, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { WorkOutline as WorkIcon } from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';

const PortalMyApplicationsScreen = ({ candidateId, candidateData, sessionToken }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('en') ? 'en-US' : 'es-CL';
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Postulación pendiente de confirmación de retiro (null = diálogo cerrado)
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);

  useEffect(() => {
    const loadApplications = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/applications`,
          {
            params: { applicantId: candidateId },
            headers: { 'x-candidate-token': sessionToken },
          }
        );

        if (response.data.response?.status_response === 'OK') {
          setApplications(response.data.response.data || []);
        } else {
          setError(t('portal.myApplications.loadError'));
        }
      } catch (err) {
        console.error(err);
        setError(t('portal.myApplications.loadError'));
      } finally {
        setLoading(false);
      }
    };

    if (candidateId) loadApplications();
  }, [candidateId, sessionToken]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    let date;
    if (timestamp._seconds) date = new Date(timestamp._seconds * 1000);
    else if (timestamp?.toDate) date = timestamp.toDate();
    else date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(dateLocale);
  };

  // Estado visible para el candidato. No se le muestra "descartado" crudo:
  // - active → "En proceso"
  // - pending_validation → "En revisión" (un reclutador revisa sus datos)
  // - discarded + withdrawnByCandidate → "Retirada por ti"
  // - discarded / blocked → "Proceso finalizado"
  const getStatusChip = (application) => {
    if (application.status === 'active') {
      return { label: t('portal.myApplications.statusActive'), color: 'primary' };
    }
    if (application.status === 'pending_validation') {
      return { label: t('portal.myApplications.statusPendingValidation'), color: 'warning' };
    }
    if (application.withdrawnByCandidate) {
      return { label: t('portal.myApplications.statusWithdrawn'), color: 'default' };
    }
    return { label: t('portal.myApplications.statusClosed'), color: 'default' };
  };

  const handleConfirmWithdraw = async () => {
    if (!withdrawTarget) return;
    const applicationId = withdrawTarget.uid || withdrawTarget.id;

    setWithdrawing(true);
    setWithdrawError(null);
    try {
      const response = await api.patch(
        `/applications/${applicationId}/withdraw`,
        {},
        { headers: { 'x-candidate-token': sessionToken } }
      );

      if (response.data.response?.status_response === 'OK') {
        setApplications((prev) =>
          prev.map((a) =>
            (a.uid || a.id) === applicationId
              ? { ...a, status: 'discarded', withdrawnByCandidate: true }
              : a
          )
        );
        setWithdrawTarget(null);
        setWithdrawSuccess(true);
      } else {
        setWithdrawError(t('portal.myApplications.withdrawError'));
      }
    } catch (err) {
      console.error(err);
      setWithdrawError(t('portal.myApplications.withdrawError'));
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 2, py: 4 }}>

      {/* Header — logo centrado como en la web de empleos */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box
          component="img"
          src="/amandalogoh.png"
          alt="Amanda HR"
          sx={{ height: 40, mb: 3 }}
        />
        <Typography variant="h5" fontWeight={700}>
          {t('portal.myApplications.title')}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error">{error}</Alert>
      )}

      {!loading && !error && applications.length === 0 && (
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 4, textAlign: 'center' }}>
          <WorkIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            {t('portal.myApplications.empty')}
          </Typography>
        </Paper>
      )}

      {withdrawSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setWithdrawSuccess(false)}>
          {t('portal.myApplications.withdrawSuccess')}
        </Alert>
      )}

      {!loading && !error && applications.length > 0 && (
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
          {applications.map((application, i) => {
            const statusChip = getStatusChip(application);
            return (
              <Box key={application.id || application.uid}>
                {i > 0 && <Divider />}
                <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {application.positionName || t('portal.myApplications.unknownPosition')}
                    </Typography>
                    {application.positionIndustria && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {application.positionIndustria}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {t('portal.myApplications.appliedOn')} {formatDate(application.createdAt)}
                    </Typography>
                    {application.status === 'active' && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => { setWithdrawError(null); setWithdrawTarget(application); }}
                        sx={{ mt: 1, textTransform: 'none', color: 'text.secondary', px: 0 }}
                      >
                        {t('portal.myApplications.withdraw')}
                      </Button>
                    )}
                  </Box>
                  <Chip
                    label={statusChip.label}
                    color={statusChip.color}
                    size="small"
                    variant={statusChip.color === 'primary' ? 'filled' : 'outlined'}
                  />
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Confirmación de retiro */}
      <Dialog open={Boolean(withdrawTarget)} onClose={() => !withdrawing && setWithdrawTarget(null)}>
        <DialogTitle>{t('portal.myApplications.withdrawConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('portal.myApplications.withdrawConfirmMessage', {
              position: withdrawTarget?.positionName || t('portal.myApplications.unknownPosition'),
            })}
          </DialogContentText>
          {withdrawError && (
            <Alert severity="error" sx={{ mt: 2 }}>{withdrawError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawTarget(null)} disabled={withdrawing} color="inherit">
            {t('portal.myApplications.withdrawCancel')}
          </Button>
          <Button
            onClick={handleConfirmWithdraw}
            disabled={withdrawing}
            color="error"
            startIcon={withdrawing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('portal.myApplications.withdrawConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PortalMyApplicationsScreen;